<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CDrawer from '@lib/components/CDrawer/CDrawer.vue'
import CDrawerContent from '@lib/components/CDrawerContent/CDrawerContent.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import { ComponentTypes } from '@canvas-builder/types/canvas'
import { getComponentTitle } from '@canvas-builder/util/canvas.util'
import useThemeVars from '@lib/composables/useThemeVars'
import useCanvas from '@canvas-builder/composables/useCanvas'

interface Props {
  extendedTitle?: string
  baseStackingContext?: number
  componentType?: ComponentTypes
  title?: string
  isCloseDisabled?: boolean
}

type Emits = {
  'update:show': [show: boolean]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const { t } = useI18n()
const themeVars = useThemeVars()
const { isSection, isTemplate } = useCanvas()

function onUpdateShow(show: boolean) {
  if (!show && props.isCloseDisabled) return
  emit('update:show', show)
}

const getCanvasTypeTitle = () => {
  if (isTemplate.value && isSection.value) {
    return t('canvasUI.common.sectionTemplate')
  } else if (isTemplate.value) {
    return t('canvasUI.common.template')
  } else if (isSection.value) {
    return t('canvasUI.common.section')
  }

  return getComponentTitle(props.componentType!, t)
}

const settingsTitle = computed(() => {
  if (props.title) {
    return props.title
  }

  let message = ''

  if (props.componentType === ComponentTypes.Root) {
    message = getCanvasTypeTitle()
  } else {
    message = getComponentTitle(props.componentType!, t)
  }

  return `${message} ${props.extendedTitle?.toLowerCase()}`.trim()
})
</script>

<template>
  <CDrawer
    class="component-drawer"
    placement="right"
    show
    :style="{
      border: `1px solid ${themeVars.primary5}`,
      'margin-top': '8px',
      'border-radius': '8px 0px 0px 8px',
    }"
    :width="360"
    :z-index="baseStackingContext"
    @update:show="onUpdateShow($event)"
  >
    <CButton
      class="component-drawer__close-button absolute top-[24px] right-[360px] z-12 rounded mr-1 h-11 w-11"
      :disabled="isCloseDisabled"
      :style="{ background: themeVars.primary5, border: `1px solid ${themeVars.primary}` }"
      @click="onUpdateShow(false)"
    >
      <template #icon>
        <CIcon icon="close" size="large" />
      </template>
    </CButton>

    <CDrawerContent :closable="false">
      <div class="flex flex-col gap-4 h-full">
        <div class="component-drawer__title">{{ settingsTitle }}</div>

        <slot />
      </div>
    </CDrawerContent>
  </CDrawer>
</template>

<style lang="scss" scoped>
.component-drawer {
  &__title {
    font-size: 20px;
    line-height: 32px;
    letter-spacing: 0.25px;
  }
}
</style>
